import { apiClient } from '../../lib/apiClient';
import type {
  BrandMemoryCreate,
  BrandMemoryDistillRead,
  BrandMemoryRead,
  BrandMemoryStateRead,
  BrandMemoryUpdate,
  BrandSuggestionAcceptRead,
  BrandSuggestionRead,
} from '../../types/api';
import { createCollectionApi, type CollectionApi } from './knowledgeBaseApi';

/** Mirror of `BrandMemoryCreate.content`'s `max_length` (ShortText). */
export const BRAND_MEMORY_MAX_LENGTH = 200;

/** Non-retired memories a business may keep. The backend enforces it; this is the copy. */
export const BRAND_MEMORY_LIMIT = 100;

/** `pending_signals` is counted up to this; at it the screen says "100+". */
export const PENDING_SIGNALS_CAP = 100;

function brandMemoriesPath(businessId: string): string {
  return `/api/v1/businesses/${businessId}/brand-memories`;
}

const base = createCollectionApi<BrandMemoryRead, BrandMemoryCreate, BrandMemoryUpdate>(
  'brand-memories',
  { updateMethod: 'PATCH' },
);

/**
 * The brand-memory collection. Same REST shape as the rest of the knowledge
 * base, except the list asks for `status=all`: the section shows suggestions,
 * active and forgotten memories together, and the default list leaves the
 * forgotten ones out.
 */
export const brandMemoriesApi: CollectionApi<
  BrandMemoryRead,
  BrandMemoryCreate,
  BrandMemoryUpdate
> = {
  ...base,
  list: (businessId, signal) =>
    apiClient.get<BrandMemoryRead[]>(brandMemoriesPath(businessId), {
      query: { status: 'all' },
      signal,
    }),
};

/** When the distiller last ran, how it went, and how many signals are waiting. */
export function fetchBrandMemoryState(
  businessId: string,
  signal?: AbortSignal,
): Promise<BrandMemoryStateRead> {
  return apiClient.get<BrandMemoryStateRead>(`${brandMemoriesPath(businessId)}/state`, {
    signal,
  });
}

/**
 * "Learn now": queues a distiller run (202). 409 `NOTHING_TO_LEARN` when no
 * signal is waiting. Repeat clicks before the run starts share one job.
 */
export function requestBrandMemoryDistill(businessId: string): Promise<BrandMemoryDistillRead> {
  return apiClient.post<BrandMemoryDistillRead>(`${brandMemoriesPath(businessId)}/distill`);
}

function brandSuggestionsPath(businessId: string): string {
  return `/api/v1/businesses/${businessId}/brand-suggestions`;
}

/** Suggestions still waiting on the owner, newest first. */
export function listBrandSuggestions(
  businessId: string,
  signal?: AbortSignal,
): Promise<BrandSuggestionRead[]> {
  return apiClient.get<BrandSuggestionRead[]>(brandSuggestionsPath(businessId), {
    query: { status: 'pending' },
    signal,
  });
}

/**
 * Accept: writes the change to the brand identity and answers with both rows.
 * 409 `SUGGESTION_NOT_PENDING` (already decided) or `SUGGESTION_CONFLICT`
 * (opposite list, or the never-say list is full); 404 when it is gone.
 */
export function acceptBrandSuggestion(
  businessId: string,
  suggestionId: string,
): Promise<BrandSuggestionAcceptRead> {
  return apiClient.post<BrandSuggestionAcceptRead>(
    `${brandSuggestionsPath(businessId)}/${suggestionId}/accept`,
  );
}

/** Dismiss: never suggested again. 409 `SUGGESTION_NOT_PENDING`; 404 when it is gone. */
export function dismissBrandSuggestion(
  businessId: string,
  suggestionId: string,
): Promise<BrandSuggestionRead> {
  return apiClient.post<BrandSuggestionRead>(
    `${brandSuggestionsPath(businessId)}/${suggestionId}/dismiss`,
  );
}
